import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'

import NodeAddressManager from '@/components/tree/node-address-manager'
import NodePaymentMethodManager from '@/components/tree/node-payment-method-manager'
import NodeServiceAreaManager from '@/components/tree/node-service-area-manager'
import NodeSettingManager from '@/components/tree/node-setting-manager'
import ServiceInstanceCredentialManager from '@/components/tree/service-instance-credential-manager'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, ORG_ORGANIZATION_PAGE_PATH, withServiceInstanceId } from '@/lib/service-instance'
import { orgTreeApi, type OrgTreeNodeConfig } from '@/services/org-tree'

const textTabs = [
  { value: 'privacy-policy', label: 'Privacy Policy', field: 'privacy_policy' as const },
  { value: 'terms-and-condition', label: 'Terms And Condition', field: 'terms_and_conditions' as const },
  { value: 'about-us', label: 'About Us', field: 'about_us' as const },
  { value: 'copyright', label: 'Copyright', field: 'copyright' as const },
  { value: 'return-policy', label: 'Return Policy', field: 'return_details' as const },
  { value: 'delivery-policy', label: 'Delivery Policy', field: 'refund_details' as const },
]

const tabOrder = [
  'address',
  ...textTabs.map((tab) => tab.value),
  'social-media',
  'contacts',
  'payments-method',
  'credentials',
  'service_area',
]

export default function TreeNodeDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'address')
  const [draftConfig, setDraftConfig] = useState<OrgTreeNodeConfig>({})

  const nodeQuery = useQuery({
    queryKey: ['org-tree-node', id],
    queryFn: () => orgTreeApi.getNode(id!),
    enabled: Boolean(id),
  })

  const configQuery = useQuery({
    queryKey: ['org-tree-config', id],
    queryFn: () => orgTreeApi.getNodeConfig(id!),
    enabled: Boolean(id),
  })

  const updateConfigMutation = useMutation({
    mutationFn: (payload: Partial<OrgTreeNodeConfig>) => orgTreeApi.updateNodeConfig(id!, payload),
    onSuccess: async () => {
      toast.success('Node details updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['org-tree-config', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  useEffect(() => {
    if (configQuery.data) {
      setDraftConfig(configQuery.data)
    }
  }, [configQuery.data])

  useEffect(() => {
    const nextTab = searchParams.get('tab') || 'address'
    if (tabOrder.includes(nextTab)) {
      setActiveTab(nextTab)
    }
  }, [searchParams])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'address') {
      nextParams.delete('tab')
    } else {
      nextParams.set('tab', value)
    }
    setSearchParams(nextParams, { replace: true })
  }

  const handleSaveTextTab = (field: keyof OrgTreeNodeConfig) => {
    updateConfigMutation.mutate({ [field]: draftConfig[field] ?? '' })
  }

  if (nodeQuery.isLoading || configQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-[28px]" />
        <Skeleton className="h-[32rem] w-full rounded-[28px]" />
      </div>
    )
  }

  if (nodeQuery.error || configQuery.error || !nodeQuery.data) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Failed to load node details</p>
        <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(nodeQuery.error || configQuery.error)}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId))}>
          Back to Tree
        </Button>
      </div>
    )
  }

  const renderTextEditor = (field: keyof OrgTreeNodeConfig, title: string, description: string) => (
    <Card className="rounded-[24px] border-border/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={String(draftConfig[field] ?? '')}
          onChange={(event) => setDraftConfig((current) => ({ ...current, [field]: event.target.value }))}
          rows={10}
        />
        <div className="flex justify-end">
          <Button onClick={() => handleSaveTextTab(field)} disabled={updateConfigMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Node Details</h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                Address, policy text, social media, contacts, and service-area data for {nodeQuery.data.node_name || `node ${nodeQuery.data.id}`}.
              </p>
            </div>

            <Button variant="outline" asChild>
              <Link to={withServiceInstanceId(`/org/node/${id}`, serviceInstanceId)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Node
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="overflow-x-auto rounded-[28px] border border-border/80 bg-card p-3">
          <TabsList className="inline-flex h-auto min-w-max gap-2 bg-transparent p-0">
            <TabsTrigger value="address">Address</TabsTrigger>
            {textTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
            ))}
            <TabsTrigger value="social-media">Social Media</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="payments-method">Wallet</TabsTrigger>
            <TabsTrigger value="credentials">Credentials</TabsTrigger>
            <TabsTrigger value="service_area">Service Area</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="address" className="mt-0">
          <NodeAddressManager nodeId={id!} />
        </TabsContent>

        {textTabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="mt-0">
            {renderTextEditor(tab.field, tab.label, `Update the ${tab.label.toLowerCase()} text for this node.`)}
          </TabsContent>
        ))}

        <TabsContent value="social-media" className="mt-0">
          <NodeSettingManager
            nodeId={id!}
            mode="social"
            title="Social Media"
            description="Social links already attached to this node."
          />
        </TabsContent>

        <TabsContent value="contacts" className="mt-0">
          <NodeSettingManager
            nodeId={id!}
            mode="contact"
            title="Contact"
            description="Contact methods already recorded against this node."
          />
        </TabsContent>

        <TabsContent value="payments-method" className="mt-0">
          <NodePaymentMethodManager nodeId={id!} orgInstanceId={serviceInstanceId} />
        </TabsContent>

        <TabsContent value="credentials" className="mt-0">
          {serviceInstanceId ? (
            <ServiceInstanceCredentialManager
              serviceInstanceId={serviceInstanceId}
              description="Manage organisation defaults and branch-specific overrides for API URLs, credentials, storage, messaging, and maps access."
            />
          ) : (
            <Card className="rounded-[24px] border-border/80">
              <CardHeader>
                <CardTitle>Credential Manager</CardTitle>
                <CardDescription>Select a service branch before editing tenant credentials.</CardDescription>
              </CardHeader>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="service_area" className="mt-0">
          <NodeServiceAreaManager nodeId={id!} serviceInstanceId={serviceInstanceId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}